import React from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';
import { SearchInput } from 'ui/standard/input/search_input/search_input';

import {
  ProtocolFilter,
  ProtocolFilterType,
} from 'services/pangolin/types';

import {
  getProtocolFilterID,
  getProtocolFilterLabel,
} from 'ui/history/protocol_entry/protocol_entry_logic';

import {
  RemovableProtocolEntry,
  SearchProtocolEntry,
  SearchProtocolEntryProps,
} from 'ui/history/protocol_entry/protocol_entry';

import baseStyles from './protocol_input.module.css';
import { ProtocolInputStore } from './protocol_input_store';
import { PortInputModal } from './port_input_modal/port_input_modal';
import { RangeInputModal } from './range_input_modal/range_input_modal';
import ProtocolInputMessages from './protocol_input_messages';

type ProtocolInputStyles = {
  inputButton: string;
  plusIcon: string;
  placeholder: string;
};

const themedStyles: ThemedStyles<ProtocolInputStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    plusIcon: baseStyles.daylightPlusIcon,
    placeholder: baseStyles.daylightPlaceholder,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    plusIcon: baseStyles.midnightPlusIcon,
    placeholder: baseStyles.midnightPlaceholder,
  },
};


type ProtocolEntryDisplayProps = {
  store: ProtocolInputStore;
  entry: ProtocolFilter;
};

const ProtocolEntryDisplay = observer(({ store, entry }: ProtocolEntryDisplayProps) => {
  const onRemoveClick = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.removeEntry(entry);
  }), [store, entry]);

  return <RemovableProtocolEntry entry={entry} onRemoveClick={onRemoveClick}/>;
});

type ProtocolInputDisplayProps = {
  store: ProtocolInputStore;
  placeholder: string;
};

const ProtocolInputDisplay = observer(({ store, placeholder }: ProtocolInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.search.setValue('');
    store.search.modal.showModal();
  }), [store]);
  return (
    <>
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.placeholder}>
          {placeholder}
        </div>
        <div className={styles.plusIcon}/>
      </button>
      {store.entries.map((entry) =>
        <ProtocolEntryDisplay store={store} entry={entry} key={getProtocolFilterID(entry)}/>)}
    </>
  );
});

type ProtocolInputProps = {
  placeholder: string;
  store: ProtocolInputStore;
};

@observer export class ProtocolInput extends React.Component<ProtocolInputProps> {
  @computed get currentEntryIDs(): Map<string, true> {
    const map: Map<string, true> = new Map();
    const ids = this.props.store.entries.map(getProtocolFilterID);
    ids.forEach((id) => map.set(id, true));
    return map;
  }

  filterEntry = (entry: ProtocolFilter, text: string): boolean => {
    switch (entry.type) {
      case ProtocolFilterType.TRANSPORT_PORT:
      case ProtocolFilterType.TRANSPORT_RANGE: {
        return true;
      }
      default: {
        if (this.currentEntryIDs.has(getProtocolFilterID(entry))) {
          return false;
        }
        const trimmed = text.trim().toLocaleLowerCase();
        if (trimmed) {
          const label = getProtocolFilterLabel(entry).toLocaleLowerCase();
          return label.indexOf(trimmed) >= 0;
        }
        return true;
      }
    }
  };

  DisplayComponent = () => {
    return <ProtocolInputDisplay store={this.props.store} placeholder={this.props.placeholder} />;
  };

  ItemComponent = ({ data: entry, ...restProps }: Omit<SearchProtocolEntryProps, 'onSelect'>) => {
    const onSelect = React.useCallback(action(() => {
      switch (entry.type) {
        case ProtocolFilterType.TRANSPORT_PORT:
          this.props.store.port.showModal();
          break;
        case ProtocolFilterType.TRANSPORT_RANGE:
          this.props.store.range.showModal();
          break;
        default:
          this.props.store.commitEntry(entry);
          this.props.store.search.modal.hideModal();
          break;
      }
    }), [this.props.store, entry]);
    return <SearchProtocolEntry data={entry} onSelect={onSelect} {...restProps}/>;
  };

  render() {
    return (
      <>
        <SearchInput
          placeholder={this.props.placeholder}
          store={this.props.store.search}
          data={this.props.store.options}
          filterFunc={this.filterEntry}
          keyFunc={getProtocolFilterID}
          ItemComponent={this.ItemComponent}
          DisplayComponent={this.DisplayComponent}
          selectOnly={false}
        />
        <PortInputModal
          store={this.props.store.port}
          onCommit={this.props.store.commitPort}
          instructions={ProtocolInputMessages.port()}
          saveMessage={ProtocolInputMessages.save()}
          invalidMessage={ProtocolInputMessages.invalidPort()}
        />
        <RangeInputModal
          store={this.props.store.range}
          onCommit={this.props.store.commitRange}
          instructions={ProtocolInputMessages.range()}
          saveMessage={ProtocolInputMessages.save()}
          invalidMessage={ProtocolInputMessages.invalidRange()}
        />
      </>
    );
  }
}
